import { useState, useCallback, useRef } from 'react';
import { useQuizFirebase } from './useQuizFirebase';
import { createLogger } from '../../../utils/logger';
// import useQuizValidation from './useQuizValidation'; // 今後の機能拡張用

// ログシステムの初期化
const logger = createLogger('QuizCore');

/**
 * クイズの核心ロジックを担当する共通フック
 * finalizeAttemptやhandleQuestionSubmitなど、重複している機能を統一
 */
const useQuizCore = () => {
  const { saveQuizResults } = useQuizFirebase();
  // const { validateQuizResponse } = useQuizValidation(); // 今後の機能拡張用

  // 状態管理
  const [isFinalizingAttempt, setIsFinalizingAttempt] = useState(false);
  const attemptFinalizedRef = useRef(false);

  /**
   * 時間切れ時の未回答問題の補完
   * @param {Array} questions - 問題配列
   * @param {Object} currentResponses - 現在の回答状況
   * @returns {Object} - 補完された回答オブジェクト
   */
  const fillUnansweredQuestions = useCallback((questions, currentResponses) => {
    const updatedResponses = { ...currentResponses };
    
    questions.forEach((question) => {
      if (!updatedResponses[question.id]) {
        updatedResponses[question.id] = {
          isCorrect: false,
          explanation: "時間切れです。",
          correctAnswer: question.type === 'sentence-rearrangement' 
            ? question.englishSentence 
            : (question.answer || ""),
          userAnswer: "",
          type: question.type,
          details: question
        };
      }
    });

    return updatedResponses;
  }, []);

  /**
   * 回答データの正規化と検証
   * @param {Array} questions - 問題配列
   * @param {Object} responses - 回答オブジェクト
   * @returns {Object} - 正規化された回答オブジェクト
   */
  const normalizeResponses = useCallback((questions, responses) => {
    const normalizedResponses = {};

    Object.keys(responses).forEach(questionId => {
      const response = responses[questionId];
      const question = questions.find(q => q.id === questionId);
      
      if (question) {
        if (question.type === 'sentence-rearrangement') {
          normalizedResponses[questionId] = {
            ...response,
            correctAnswer: question.englishSentence,
            userAnswer: response.userAnswer || "",
            details: {
              ...question,
              englishSentence: question.englishSentence,
              japaneseTranslation: question.japaneseTranslation
            }
          };
        } else if (question.type === 'fill-in-the-blank') {
          normalizedResponses[questionId] = {
            ...response,
            userAnswer: response.userAnswer || "",
            details: {
              type: question.type,
              question: question.question,
              answer: question.answer,
              explanation: question.explanation || "",
              userResponse: {
                isCorrect: response.isCorrect,
                explanation: response.explanation,
                userAnswer: response.userAnswer || "",
                correctAnswer: question.answer
              }
            }
          };
        } else {
          normalizedResponses[questionId] = {
            ...response,
            userAnswer: response.userAnswer || "",
            details: question
          };
        }
      }
    });

    return normalizedResponses;
  }, []);

  /**
   * クイズ結果の確定処理（統一版finalizeAttempt）
   * @param {Object} params - 確定パラメータ
   * @param {Array} params.questions - 問題配列
   * @param {Object} params.responses - 回答オブジェクト
   * @param {string} params.studentId - 学生ID
   * @param {string} params.contentId - コンテンツID
   * @param {number} params.startTime - 開始時間
   * @param {boolean} params.forced - 強制確定フラグ（時間切れ等）
   * @param {Function} params.onSuccess - 成功時のコールバック
   * @param {Function} params.onError - エラー時のコールバック
   * @returns {Promise<Object>} - 確定結果
   */
  const finalizeQuizAttempt = useCallback(async ({
    questions,
    responses,
    studentId,
    contentId,
    startTime,
    forced = false,
    onSuccess = null,
    onError = null
  }) => {
    logger.info('🎯 useQuizCore - finalizeQuizAttempt 開始', { forced, responsesCount: Object.keys(responses).length });
    
    if (isFinalizingAttempt || attemptFinalizedRef.current) {
      logger.info('🛑 useQuizCore - 重複実行防止により処理停止');
      return { success: false, message: '既に処理中です' };
    }

    setIsFinalizingAttempt(true);
    attemptFinalizedRef.current = true;

    try {
      const total = questions.length;
      const answeredCount = Object.keys(responses).length;
      
      // 全問回答チェック（時間切れでない場合は必須）
      if (!forced && answeredCount < total) {
        logger.info('🔴 useQuizCore - 全問未回答のため結果記録停止');
        setIsFinalizingAttempt(false);
        attemptFinalizedRef.current = false;
        
        const errorResult = {
          success: false,
          message: '全ての問題を解いていないため、結果は記録されませんでした。'
        };
        
        if (onError) onError(errorResult);
        return errorResult;
      }

      // 時間切れの場合、未回答問題を補完
      let finalResponses = responses;
      if (forced) {
        finalResponses = fillUnansweredQuestions(questions, responses);
        logger.info('🕒 useQuizCore - 時間切れにより未回答問題を補完');
      }

      // 回答データの正規化
      const normalizedResponses = normalizeResponses(questions, finalResponses);

      // Firebase保存実行
      const saveResult = await saveQuizResults({
        studentId,
        modalContentId: contentId,
        questions,
        responses: normalizedResponses,
        startTime,
        forced
      });

      if (saveResult.success) {
        logger.info('✅ useQuizCore - 結果保存成功:', saveResult);
        
        const successResult = {
          success: true,
          ...saveResult,
          finalResponses: normalizedResponses
        };
        
        if (onSuccess) onSuccess(successResult);
        return successResult;
      } else {
        logger.error('🔴 useQuizCore - 結果保存失敗:', saveResult);
        
        const errorResult = {
          success: false,
          message: saveResult.message || '結果の保存に失敗しました'
        };
        
        if (onError) onError(errorResult);
        return errorResult;
      }

    } catch (error) {
      logger.error('🚨 useQuizCore - finalizeQuizAttempt エラー:', error);
      
      const errorResult = {
        success: false,
        message: '予期しないエラーが発生しました',
        error: error.message
      };
      
      if (onError) onError(errorResult);
      return errorResult;
    } finally {
      setIsFinalizingAttempt(false);
    }
  }, [isFinalizingAttempt, saveQuizResults, fillUnansweredQuestions, normalizeResponses]);

  /**
   * 単一問題の回答処理（統一版handleQuestionSubmit）
   * @param {Object} params - 回答パラメータ
   * @param {Object} params.question - 問題オブジェクト
   * @param {boolean} params.isCorrect - 正解フラグ
   * @param {string} params.explanation - 解説
   * @param {string} params.correctAnswer - 正解
   * @param {string} params.userAnswer - ユーザー回答
   * @param {Object} params.currentResponses - 現在の回答状況
   * @param {Function} params.onResponseUpdate - 回答更新時のコールバック
   * @returns {Object} - 更新された回答オブジェクト
   */
  const submitQuestionAnswer = useCallback(({
    question,
    isCorrect,
    explanation,
    correctAnswer,
    userAnswer,
    currentResponses,
    onResponseUpdate = null
  }) => {
    logger.info('📝 useQuizCore - submitQuestionAnswer 開始:', { questionId: question.id, isCorrect });

    const updatedResponses = {
      ...currentResponses,
      [question.id]: { 
        isCorrect, 
        explanation, 
        correctAnswer,
        userAnswer,
        type: question.type,
        details: question
      }
    };

    if (onResponseUpdate) {
      onResponseUpdate(updatedResponses);
    }

    logger.info('✅ useQuizCore - 回答更新完了');
    return updatedResponses;
  }, []);

  /**
   * 全問回答完了チェック
   * @param {Array} questions - 問題配列
   * @param {Object} responses - 回答オブジェクト
   * @returns {boolean} - 全問回答完了フラグ
   */
  const checkAllAnswered = useCallback((questions, responses) => {
    return questions.length > 0 && questions.every(q => responses[q.id]);
  }, []);

  /**
   * 確定状態のリセット
   */
  const resetFinalizationState = useCallback(() => {
    logger.info('🔄 useQuizCore - 確定状態リセット');
    setIsFinalizingAttempt(false);
    attemptFinalizedRef.current = false;
  }, []);

  return {
    // 状態
    isFinalizingAttempt,
    isAttemptFinalized: attemptFinalizedRef.current,

    // 核心機能
    finalizeQuizAttempt,
    submitQuestionAnswer,
    
    // ユーティリティ
    checkAllAnswered,
    fillUnansweredQuestions,
    normalizeResponses,
    resetFinalizationState
  };
};

export default useQuizCore; 